import { useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { debounce } from '@mui/material/utils';
import { FieldValues, useController } from 'react-hook-form';

import {
    IBasicOfferEventListItemForCertificateFragment,
    OfferEventsForCertificateDocument,
} from 'graphql/types';
import { TControllerProps } from 'common/types';
import { asTypedControl } from 'common/utils/form';
import { getAbsoluteDate } from 'common/utils/formatDate';

const BASE_QUERY_VARS = {
    first: 50,
};

interface IProps {
    canEnrollParticipants?: boolean;
    certificateId?: string;
    disabled?: boolean;
    label?: string;
    name: string;
    noOptionsText?: string;
    enrollableForUser?: string;
}

export const CertificateOfferEventSelectField = <
    TFieldValues extends FieldValues,
>({
    canEnrollParticipants,
    certificateId,
    control: formControl,
    disabled,
    label,
    name,
    noOptionsText,
    enrollableForUser,
}: IProps & TControllerProps<TFieldValues>) => {
    const [translate] = useTranslation();

    const control =
        asTypedControl<IBasicOfferEventListItemForCertificateFragment>(
            name,
            formControl
        );

    const {
        field,
        fieldState: { error },
    } = useController({ name, control });

    const [inputValue, setInputValue] = useState('');

    // Only ask for events from today onwards so events whose enrollment has
    // closed (past meetings/webinars) drop out.
    const queryVars = useMemo(
        () => ({
            ...BASE_QUERY_VARS,
            fromDate: DateTime.now().startOf('day').toISODate(),
            canEnrollParticipants,
            enrollableForUser,
        }),
        [canEnrollParticipants, enrollableForUser]
    );

    const [fetchOfferEvents, { data, loading, called }] = useLazyQuery(
        OfferEventsForCertificateDocument,
        { fetchPolicy: 'no-cache' }
    );

    const { paginatedOfferEvents } = data || {};

    const offerEvents =
        paginatedOfferEvents?.edges
            ?.map((edge) => edge?.node)
            .filter(Boolean) || [];
    const offerEventsCount = paginatedOfferEvents?.count || 0;

    const value: IBasicOfferEventListItemForCertificateFragment | null =
        field.value;

    const fetch = useMemo(
        () =>
            debounce((inputValue: string) => {
                if (!certificateId) return;

                fetchOfferEvents({
                    variables: {
                        ...queryVars,
                        certificateId,
                        q: inputValue || undefined,
                    },
                });
            }, 400),
        [certificateId, fetchOfferEvents, queryVars]
    );

    // Cancel the previous debounced fetch when certificateId changes so a
    // pending call cannot fire with the old id and race the immediate refetch.
    useEffect(() => () => fetch.clear(), [fetch]);

    // Refetch immediately when the selected certificate changes so the
    // dropdown does not show events from the previously selected certificate.
    // Reads `inputValue` and `fetchOfferEvents` from the closure on purpose:
    // input typing is handled by the debounced effect below.
    useEffect(() => {
        if (!called || !certificateId) return;

        fetchOfferEvents({
            variables: {
                ...queryVars,
                certificateId,
                q: inputValue || undefined,
            },
        });
    }, [certificateId]);

    // Debounced refetch when the user types. `fetch` is intentionally omitted
    // from deps: it is recreated when certificateId changes, and that case is
    // handled by the effect above to avoid a double fetch.
    useEffect(() => {
        if (!called) return;

        fetch(inputValue);
    }, [inputValue]);

    const handleOpen = () => {
        if (!certificateId) return;

        if (!!offerEvents.length) return;

        fetchOfferEvents({
            variables: {
                ...queryVars,
                certificateId,
                q: inputValue || undefined,
            },
        });
    };

    const getDateLine = (
        offerEvent:
            | IBasicOfferEventListItemForCertificateFragment
            | null
            | undefined
    ) => {
        if (!offerEvent?.firstDate) return null;

        const dateDisplay = getAbsoluteDate(offerEvent.firstDate.startDate);
        const moreCount = Math.max(0, (offerEvent.dates?.length || 0) - 1);
        const moreText =
            moreCount > 0
                ? ` - ${translate('moreSessions', { count: moreCount })}`
                : '';

        return `${dateDisplay}${moreText}`;
    };

    const isDisabled = disabled || !certificateId;
    const selectedDateLine = getDateLine(value);
    const showSelectedOverlay = !!value && !!selectedDateLine && !inputValue;

    return (
        <Autocomplete
            disabled={isDisabled}
            filterOptions={(options) =>
                options as IBasicOfferEventListItemForCertificateFragment[]
            }
            getOptionLabel={(option) =>
                (option as IBasicOfferEventListItemForCertificateFragment)
                    .offerEventTitle
            }
            isOptionEqualToValue={(option, value) =>
                (option as IBasicOfferEventListItemForCertificateFragment)
                    .id ===
                (value as IBasicOfferEventListItemForCertificateFragment).id
            }
            loading={loading}
            loadingText={translate('loadingDots')}
            noOptionsText={
                !loading
                    ? (noOptionsText ?? translate('noOptionsText.offers'))
                    : translate('loadingDots')
            }
            options={offerEvents}
            renderInput={(params) => (
                <Box sx={{ position: 'relative' }}>
                    <TextField
                        {...params}
                        error={!!error}
                        helperText={error?.message}
                        label={label ?? translate('offer')}
                        margin="normal"
                        slotProps={{
                            input: {
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loading && (
                                            <CircularProgress
                                                color="inherit"
                                                size={20}
                                                sx={{ mr: 1 }}
                                            />
                                        )}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                                sx:
                                    showSelectedOverlay && selectedDateLine
                                        ? {
                                              alignItems: 'flex-start',
                                              minHeight: 68,
                                              pt: '8px',
                                          }
                                        : undefined,
                            },
                        }}
                        sx={{
                            '& .MuiInputBase-input': {
                                color: showSelectedOverlay
                                    ? 'transparent'
                                    : undefined,
                            },
                        }}
                        variant="outlined"
                    />

                    {showSelectedOverlay && value && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 24,
                                left: 14,
                                right: 56,
                                pointerEvents: 'none',
                            }}
                        >
                            <Typography>{value.offerEventTitle}</Typography>

                            {selectedDateLine && (
                                <Typography
                                    sx={{
                                        color: 'grey.500',
                                        display: 'block',
                                        mt: -1,
                                    }}
                                    variant="caption"
                                >
                                    {selectedDateLine}
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>
            )}
            renderOption={({ key, ...props }, option) => {
                const offerEvent =
                    option as IBasicOfferEventListItemForCertificateFragment;
                const dateLine = getDateLine(offerEvent);

                return (
                    <Box component="li" key={key} {...props}>
                        <Box sx={{ width: '100%' }}>
                            <Typography>
                                {offerEvent.offerEventTitle}
                            </Typography>

                            {dateLine && (
                                <Typography
                                    sx={{
                                        color: 'grey.500',
                                        display: 'block',
                                        mt: -1,
                                    }}
                                    variant="caption"
                                >
                                    {dateLine}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                );
            }}
            slotProps={{
                listbox: {
                    component: ({
                        children,
                        ...other
                    }: React.ComponentProps<'ul'>) => (
                        <ul {...other}>
                            {children}
                            {offerEventsCount > BASE_QUERY_VARS.first && (
                                <Box
                                    className="MuiAutocomplete-option"
                                    component="li"
                                    key="hasMore"
                                    sx={{
                                        pointerEvents: 'none',
                                        opacity: 0.6,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            width: '100%',
                                            textAlign: 'center',
                                        }}
                                        variant="caption"
                                    >
                                        {translate('moreResultsSelectSearch')}
                                    </Typography>
                                </Box>
                            )}
                        </ul>
                    ),
                },
            }}
            value={value || null}
            onChange={(_event, value) => {
                field.onChange(value);
            }}
            onInputChange={(_event, newInputValue, reason) => {
                if (reason === 'input' || reason === 'clear') {
                    setInputValue(newInputValue);
                }
            }}
            onOpen={handleOpen}
        />
    );
};
